import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { listKeys, createKey, deleteKey } from "../api/agentApi";
import { useUser } from "./UserContext";
import { PERMISSIONS } from "../permissions";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

interface ApiKey {
  id: string;
  name: string;
  key_type: string;
  description?: string;
  provider: string;
  created_at: string;
  last_used?: string;
  status: "active" | "inactive" | "expired";
  grant_type?: string;
  scope?: string;
  expires_at?: string;
}

export default function KeyManager() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [deleting, setDeleting] = useState("");
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [newKey, setNewKey] = useState({
    name: "",
    description: "",
    value: "",
    provider: "",
    key_type: "api_key",
    client_id: "",
    client_secret: "",
    access_token: "",
    refresh_token: "",
    token_url: "",
    authorization_url: "",
    redirect_uri: "",
    scope: "",
    auth_method: "client_secret_basic",
    expires_at: "",
  });
  const [isAddingKey, setIsAddingKey] = useState(false);
  const { can } = useUser();
  const canManageKeys = Boolean(can?.(PERMISSIONS.CONFIG_KEYS_MANAGE));

  // Fetch keys from API
  const fetchKeys = async () => {
    try {
      const res = await listKeys();
      setKeys(res.keys || []);
    } catch (e) {
      setError("Failed to fetch keys.");
    }
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!canManageKeys) return;
    const params = new URLSearchParams(window.location.search);
    const shouldOpen = params.get("open") === "1" || Boolean(params.get("required_env"));
    if (!shouldOpen) return;

    const requiredEnv = params.get("required_env") || "";
    const provider = params.get("provider_label") || params.get("provider") || "";
    const name = params.get("name") || requiredEnv || (provider ? `${provider}_api_key` : "");
    setNewKey((current) => ({
      ...current,
      name,
      provider,
      description: requiredEnv
        ? `Credential used by tools that require ${requiredEnv}.`
        : current.description,
      key_type: "api_key",
    }));
    setOpenDialog(true);
  }, [canManageKeys]);

  const handleDelete = async (keyId: string) => {
    if (!window.confirm(`Delete key '${keyId}'? This cannot be undone.`)) return;
    setDeleting(keyId);
    try {
      await deleteKey(keyId);
      await fetchKeys();
    } catch (e) {
      setError("Failed to delete key.");
    }
    setDeleting("");
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewKey({
      name: "",
      description: "",
      value: "",
      provider: "",
      key_type: "api_key",
      client_id: "",
      client_secret: "",
      access_token: "",
      refresh_token: "",
      token_url: "",
      authorization_url: "",
      redirect_uri: "",
      scope: "",
      auth_method: "client_secret_basic",
      expires_at: "",
    });
  };

  const handleAddKey = async () => {
    setError("");
    if (!newKey.name) {
      setError("Key name is required.");
      return;
    }
    if (newKey.key_type === "api_key" && !newKey.value) {
      setError("Key value is required.");
      return;
    }
    if (newKey.key_type === "oauth_access_token" && !(newKey.access_token || newKey.value)) {
      setError("Access token is required.");
      return;
    }
    if (newKey.key_type === "oauth_authorization_code" && !newKey.token_url) {
      setError("Token URL is required for authorization-code OAuth credentials.");
      return;
    }
    if (newKey.key_type === "oauth_authorization_code" && !(newKey.access_token || newKey.refresh_token || newKey.value)) {
      setError("Provide an access token or refresh token for authorization-code OAuth credentials.");
      return;
    }
    if (newKey.key_type === "oauth_client_credentials" && !(newKey.client_id && newKey.client_secret && newKey.token_url)) {
      setError("Client ID, client secret, and token URL are required for client-credentials OAuth.");
      return;
    }
    setIsAddingKey(true);
    try {
      const payload: any = {
        name: newKey.name,
        description: newKey.description,
        provider: newKey.provider,
        key_type: newKey.key_type,
        value: newKey.value || undefined,
        client_id: newKey.client_id || undefined,
        client_secret: newKey.client_secret || undefined,
        access_token: newKey.access_token || undefined,
        refresh_token: newKey.refresh_token || undefined,
        token_url: newKey.token_url || undefined,
        authorization_url: newKey.authorization_url || undefined,
        redirect_uri: newKey.redirect_uri || undefined,
        scope: newKey.scope || undefined,
        auth_method: newKey.auth_method || undefined,
        expires_at: newKey.expires_at || undefined,
      };
      await createKey(payload);
      await fetchKeys();
      handleCloseDialog();
    } catch (e: any) {
      setError(e?.message || "Failed to add key.");
    } finally {
      setIsAddingKey(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 1,
        }}
      >
        <Typography variant="h4">Key Manager</Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {canManageKeys && (
        <Button
          onClick={handleOpenDialog}
          variant="contained"
          color="primary"
          sx={{
            mt: 3,
            fontSize: "1rem",
            px: 3,
            py: 1,
            borderRadius: "99px",
            textTransform: "none",
          }}
        >
          Add Credential
        </Button>
      )}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Key Name
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Key Type
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem", width: 400 }}>
                Description
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Provider
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Created
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Last Used
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No credentials found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {canManageKeys
                      ? "Click 'Add Keys' to create your first credential."
                      : "No keys have been configured yet."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              keys.map((key) => (
              <TableRow key={key.id} hover sx={{ fontSize: "0.95rem" }}>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  {key.name}
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  <Chip
                    label={key.key_type.replace(/_/g, ' ')}
                    size="small"
                    sx={{
                      bgcolor: key.key_type.startsWith("oauth") ? "#f4efe8" : "#e3f2fd",
                      color: key.key_type.startsWith("oauth") ? "#9a5f2b" : "#1976d2",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  {key.description || "—"}
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  {key.provider}
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  {key.created_at}
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  {key.last_used || "Never"}
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Button
                    startIcon={<DeleteIcon />}
                    size="small"
                    color="error"
                    onClick={() => handleDelete(key.id)}
                    disabled={deleting === key.id}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Key Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Credential</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {error && (
              <Alert severity="error">{error}</Alert>
            )}
            <TextField
              label="Credential name"
              value={newKey.name}
              onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
              fullWidth
              required
              autoComplete="off"
            />
            <TextField
              select
              label="Credential type"
              value={newKey.key_type}
              onChange={(e) => setNewKey({ ...newKey, key_type: e.target.value })}
              fullWidth
            >
              <MenuItem value="api_key">API key</MenuItem>
              <MenuItem value="oauth_access_token">OAuth access token</MenuItem>
              <MenuItem value="oauth_authorization_code">OAuth authorization-code token bundle</MenuItem>
              <MenuItem value="oauth_client_credentials">OAuth client credentials</MenuItem>
            </TextField>
            <Autocomplete
              freeSolo
              options={[
                "OpenAI",
                "Anthropic",
                "Google",
                "AWS",
                "Azure",
                "HuggingFace",
                "Cohere",
                "Mistral",
                "Gemini",
                "GitHub",
                "GitLab",
                "Slack",
                "Microsoft",
                "Salesforce",
                "Stripe",
                "Twilio",
                "SendGrid",
                "PostgreSQL",
                "MongoDB",
                "Redis",
                "Brex",
              ]}
              value={newKey.provider}
              onChange={(_, newValue) => setNewKey({ ...newKey, provider: newValue || "" })}
              onInputChange={(_, newInputValue) => setNewKey({ ...newKey, provider: newInputValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Provider (optional)"
                  placeholder="Select or type a custom provider"
                />
              )}
            />
            <TextField
              label="Description (optional)"
              value={newKey.description}
              onChange={(e) => setNewKey({ ...newKey, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              autoComplete="off"
            />
            {newKey.key_type === "api_key" && (
              <TextField
                label="API key value"
                value={newKey.value}
                onChange={(e) => setNewKey({ ...newKey, value: e.target.value })}
                fullWidth
                required
                type="password"
                autoComplete="off"
              />
            )}
            {newKey.key_type !== "api_key" && (
              <>
                <TextField
                  label="Token URL"
                  value={newKey.token_url}
                  onChange={(e) => setNewKey({ ...newKey, token_url: e.target.value })}
                  fullWidth
                  required={newKey.key_type !== "oauth_access_token"}
                  autoComplete="off"
                />
                {newKey.key_type === "oauth_authorization_code" && (
                  <>
                    <TextField
                      label="Authorization URL (optional)"
                      value={newKey.authorization_url}
                      onChange={(e) => setNewKey({ ...newKey, authorization_url: e.target.value })}
                      fullWidth
                      autoComplete="off"
                    />
                    <TextField
                      label="Redirect URI (optional)"
                      value={newKey.redirect_uri}
                      onChange={(e) => setNewKey({ ...newKey, redirect_uri: e.target.value })}
                      fullWidth
                      autoComplete="off"
                    />
                  </>
                )}
                {(newKey.key_type === "oauth_authorization_code" || newKey.key_type === "oauth_client_credentials") && (
                  <>
                    <TextField
                      label="Client ID"
                      value={newKey.client_id}
                      onChange={(e) => setNewKey({ ...newKey, client_id: e.target.value })}
                      fullWidth
                      required={newKey.key_type === "oauth_client_credentials"}
                      autoComplete="off"
                    />
                    <TextField
                      label="Client secret"
                      value={newKey.client_secret}
                      onChange={(e) => setNewKey({ ...newKey, client_secret: e.target.value })}
                      fullWidth
                      required={newKey.key_type === "oauth_client_credentials"}
                      type="password"
                      autoComplete="off"
                    />
                  </>
                )}
                {(newKey.key_type === "oauth_access_token" || newKey.key_type === "oauth_authorization_code") && (
                  <>
                    <TextField
                      label="Access token"
                      value={newKey.access_token}
                      onChange={(e) => setNewKey({ ...newKey, access_token: e.target.value })}
                      fullWidth
                      required={newKey.key_type === "oauth_access_token"}
                      type="password"
                      autoComplete="off"
                    />
                    <TextField
                      label="Refresh token (optional)"
                      value={newKey.refresh_token}
                      onChange={(e) => setNewKey({ ...newKey, refresh_token: e.target.value })}
                      fullWidth
                      type="password"
                      autoComplete="off"
                    />
                  </>
                )}
                <TextField
                  label="Scopes (optional)"
                  value={newKey.scope}
                  onChange={(e) => setNewKey({ ...newKey, scope: e.target.value })}
                  fullWidth
                  placeholder="channels:read chat:write files:read"
                  autoComplete="off"
                />
                <TextField
                  label="Access token expiry (optional)"
                  value={newKey.expires_at}
                  onChange={(e) => setNewKey({ ...newKey, expires_at: e.target.value })}
                  fullWidth
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  label="Client auth method"
                  value={newKey.auth_method}
                  onChange={(e) => setNewKey({ ...newKey, auth_method: e.target.value })}
                  fullWidth
                >
                  <MenuItem value="client_secret_basic">client_secret_basic</MenuItem>
                  <MenuItem value="client_secret_post">client_secret_post</MenuItem>
                  <MenuItem value="none">none</MenuItem>
                </TextField>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit" disabled={isAddingKey}>
            Cancel
          </Button>
          <Button onClick={handleAddKey} variant="contained" color="primary" disabled={isAddingKey} startIcon={isAddingKey ? <CircularProgress size={20} /> : null}>
            {isAddingKey ? "Adding..." : "Save Credential"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
